"use client";

import type { ReactNode } from "react";
import { createContext, useContext, useState } from "react";

interface IMediaProviderProps {
  children: ReactNode;
}

type MediaContextType = {
  selectedItems: string[];
  selectItem: (id: string) => void;
  selectItems: (ids: string[]) => void;
  toggleAll: (ids: string[]) => void;
  isSelected: (id: string) => boolean;
  clearSelectedMedia: VoidFunction;
  pageSizeState: number;
  setPageSizeState: (pageSize: number) => void;
};

const MediaContext = createContext({} as MediaContextType);
export function useMediaContext() {
  return useContext(MediaContext);
}

export function MediaProvider({ children }: IMediaProviderProps) {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [pageSizeState, setPageSizeState] = useState<number>(1)

  function isSelected(id: string) {
    return Boolean(selectedItems.find((item: string) => item === id));
  }

  function selectItem(id: string) {
    if (isSelected(id)) {
      setSelectedItems((prevItems) => {
        return prevItems.filter((item: string) => item !== id);
      });
    } else {
      setSelectedItems((prevItems) => [...prevItems, id]);
    }
  }

  function selectItems(ids: string[]) {
    setSelectedItems(ids);
  }

  function toggleAll(items: string[]): void {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems([...items]);
    }
  }

  function clearSelectedMedia() {
    setSelectedItems([]);
  }

  return (
    <MediaContext.Provider
      value={{
        selectedItems,
        isSelected,
        selectItem,
        selectItems,
        toggleAll,
        clearSelectedMedia,
        pageSizeState,
        setPageSizeState
      }}
    >
      {children}
    </MediaContext.Provider>
  );
}
